import PropTypes from "prop-types"
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react"
import queryString from "query-string"

import Section from "@atoms/Section/Section"
import {
  getSavedState as getPricesSavedState,
  usePricesContext,
} from "@context/prices"
import {
  useSearchContext,
  getInitialStateFromSearchParam,
} from "@context/search"
import { SET_INITIAL_STATE } from "@context/search/actions"
import { useSearchResultContext } from "@context/searchResult"
import {
  filterSites,
  FILTER_SITES,
  CLEAR_FILTERS,
} from "@context/searchResult/actions"
import {
  getSavedState as getWishListSavedState,
  useWishListContext,
} from "@context/wishList"
import SearchResultFilters from "@organisms/SearchResultFilters/SearchResultFilters"
import SitesList from "@organisms/SitesList/SitesList"
import { SitesListProvider } from "@context/sitesList"

import styles from "./SearchResultWidget.module.scss"

const SearchResultWidget = ({ results }) => {
  const {
    state: { unitArea, availability, filtersHighlighted },
    dispatch: searchDispatch,
  } = useSearchContext()

  const [focusedSiteId, setFocusedSiteId] = useState(null)

  const {
    state: {
      sitesFiltered,
      priceRange,
      searchTerm,
      siteDistricts,
      siteTypes,
      siteServices,
      sitePublicTransports,
      siteParking,
      unitAttributes,
      unitTechnicalAttributes,
    },
    dispatch: resultDispatch,
  } = useSearchResultContext()
  const { dispatch: wishListDispatch } = useWishListContext()
  const { dispatch: pricesDispatch } = usePricesContext()

  useEffect(() => {
    const searchState = getInitialStateFromSearchParam()
    getWishListSavedState(wishListDispatch)
    getPricesSavedState(pricesDispatch)

    if (searchState) {
      searchDispatch({ type: SET_INITIAL_STATE, payload: searchState })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onFilterSave = useCallback(() => {
    filterSites(
      results,
      {
        searchTerm,
        priceRange,
        siteDistricts,
        siteTypes,
        siteServices,
        sitePublicTransports,
        siteParking,
        unitAttributes,
        unitTechnicalAttributes,
        unitArea,
        availability,
        focusedSiteId,
      },
      (data) => resultDispatch({ type: FILTER_SITES, payload: data })
    )
  }, [
    resultDispatch,
    results,
    searchTerm,
    priceRange,
    siteDistricts,
    siteParking,
    sitePublicTransports,
    siteServices,
    siteTypes,
    unitAttributes,
    unitTechnicalAttributes,
    unitArea,
    availability,
    focusedSiteId,
  ])

  const onFilterClear = useCallback(() => {
    resultDispatch({ type: CLEAR_FILTERS })
  }, [resultDispatch])

  // This line for eslint disable is needed because if the dependency array is not empty it triggers more than once and createas a loop
  useEffect(() => {
    filterSites(
      results,
      {
        searchTerm,
        priceRange,
        siteDistricts,
        siteTypes,
        siteServices,
        sitePublicTransports,
        siteParking,
        unitAttributes,
        unitTechnicalAttributes,
        unitArea,
        availability,
        focusedSiteId,
      },
      (data) => resultDispatch({ type: FILTER_SITES, payload: data })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitArea, availability])

  useEffect(() => {
    setFocusedSiteId(queryString.parse(window.location.search).siteId || null)
  }, [])

  const listFallbackRef = useRef([])

  useMemo(() => {
    listFallbackRef.current = results
  }, [results])
  const listFallback = listFallbackRef.current

  return (
    <Section breakOut margins="my-0">
      <div
        className={`relative ${
          filtersHighlighted ? styles.containerFiltersHighlighted : ""
        }`}
      >
        <Section breakOut margins="my-0">
          <div className="relative z-30 bg-white">
            <div className="container mx-auto">
              <SearchResultFilters
                onSave={onFilterSave}
                onClear={onFilterClear}
              />
            </div>
          </div>
        </Section>
        <Section breakOut margins="my-0">
          <SitesListProvider>
            <SitesList
              listFallback={listFallback}
              list={sitesFiltered}
              focusedSiteId={focusedSiteId}
              withTotalRentableArea={false}
              showLeadooNoMoreResultsBot={true}
            />
          </SitesListProvider>
        </Section>
      </div>
    </Section>
  )
}

export default SearchResultWidget

SearchResultWidget.propTypes = {
  results: PropTypes.array.isRequired,
}
